import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Four-pillar strip - substantiates the hero promise AND acts as the primary
// "do something" entry row: each pillar links into the relevant part of the
// product (find people, browse suppliers, read guides, open the tools).
// Bespoke 3D illustrated icons match the premium toolbox-icon style.
const PILLARS = [
  { key: 'find', img: '/images/pillar-icons/people.webp', title: 'Find', body: 'Find the right people and products for your project.', to: '/find' },
  { key: 'learn', img: '/images/pillar-icons/information.webp', title: 'Learn', body: 'Understand your options and make informed decisions at every stage.', to: '/learn' },
  { key: 'build', img: '/images/pillar-icons/build.webp', title: 'Build', body: 'Explore home designs and house-and-land options for your future home.', to: '/home-designs' },
  { key: 'tools', img: '/images/pillar-icons/tools.webp', title: 'Tools', body: 'Plan, organise and track your project in your personal workspace.', to: '/workspace' },
];

const FourPillarsStrip = () => (
  <section className="bg-white border-b border-gray-100 py-10 sm:py-16" data-testid="four-pillars-strip">
    <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-[640px] mx-auto text-center mb-8 sm:mb-12">
        <h2 className="text-[28px] sm:text-[38px] font-bold text-[#1d1d1f] tracking-[-0.02em] leading-[1.12]">
          Everything a build needs, <span className="gradient-text-orange">in one place</span>
        </h2>
        <p className="text-[14px] sm:text-[16px] text-[#86868b] mt-2.5 leading-[1.55]">
          Build, renovate or improve with the four things that actually move a project forward.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 sm:gap-x-8 lg:gap-x-10 gap-y-9 sm:gap-y-10" data-testid="four-pillars-grid">
        {PILLARS.map((p) => (
          <Link
            key={p.key}
            to={p.to}
            className="group text-center rounded-2xl p-2 -m-2 transition-colors hover:bg-[#fafbfc] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1d8efe] focus-visible:ring-offset-2"
            data-testid={`four-pillar-${p.key}`}
          >
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <img
                src={p.img}
                alt={`${p.title} icon`}
                loading="lazy"
                decoding="async"
                className="w-12 h-12 sm:w-14 sm:h-14 object-contain transition-transform duration-200 group-hover:scale-110"
              />
            </div>
            <h3 className="text-[16px] sm:text-[19px] font-bold text-[#1d1d1f] mb-1.5 group-hover:text-[#1d8efe] transition-colors">{p.title}</h3>
            <p className="text-[12.5px] sm:text-[14px] text-[#86868b] leading-[1.5]">{p.body}</p>
          </Link>
        ))}
      </div>

      <div className="text-center mt-9 sm:mt-12">
        <Link
          to="/where-we-fit-in"
          className="inline-flex items-center gap-1.5 text-[#1d8efe] hover:underline font-medium text-[14px] sm:text-[16px] transition-all group"
          data-testid="four-pillars-where-we-fit"
        >
          See where we fit in
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      </div>
    </div>
  </section>
);

export default FourPillarsStrip;
